"use strict";
// An area's surfaces, as stored in hig_measurearea.hig_surfaces (JSON —
// docs/SCHEMA.md "Surfaces JSON"; v2 adds spray, repeat groups and assessed
// hours, "Before UAT"; v3 the area's Wash and Wash only flags, #510; v4 the reason for Other approved hours; v5 the PM's
// hours fixed when Assessed is chosen, #607). Pure functions only: every change returns a new
// document, and the area editor saves it like any other field.
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeGroup = exports.updateGroup = exports.removeLine = exports.updateLine = exports.addLine = exports.updateAssessment = exports.setPaintFlag = exports.setUnit = exports.updateSettings = exports.removeSurface = exports.serialiseSurfaces = exports.setWashOnly = exports.setWash = exports.isHoursById = exports.surfaceLabel = exports.SURFACE_UNIT = void 0;
exports.parseSurfaces = parseSurfaces;
exports.setApprovedAssessed = setApprovedAssessed;
exports.setApprovedReason = setApprovedReason;
exports.addSurface = addSurface;
exports.renameSurface = renameSurface;
exports.addGroup = addGroup;
exports.duplicateGroup = duplicateGroup;
exports.copySurfaces = copySurfaces;
exports.surfaceCount = surfaceCount;
/**
 * Written by this version. v1 to v4 are still read (with none of the later extras) and become v5 on their next save.
 * v3 (#510) adds the wash flags, v4 `approvedReason` and v5 `approvedAssessed` (approved-hours.ts): an older build refuses
 * the area rather than saving it back without them.
 */
const SURFACES_SCHEMA_VERSION = 5;
const READABLE_VERSIONS = [1, 2, 3, 4, SURFACES_SCHEMA_VERSION];
/** hig_surfacetype.hig_unit's choices, as the API labels them. */
exports.SURFACE_UNIT = { m2: 'm²', lm: 'LM', each: 'Each' };
/** A surface's name wherever it is shown: its own, else its type's. */
const surfaceLabel = (surface) => surface.name ?? surface.typeName;
exports.surfaceLabel = surfaceLabel;
const emptySurfaces = () => ({ schemaVersion: SURFACES_SCHEMA_VERSION, surfaces: [], groups: [] });
/** The stored text as a document. Empty = no surfaces yet; an unknown version is refused rather than misread. */
function parseSurfaces(text) {
    if (!text)
        return emptySurfaces();
    const parsed = JSON.parse(text);
    if (!READABLE_VERSIONS.includes(parsed.schemaVersion) || !Array.isArray(parsed.surfaces)) {
        throw new Error(`This area's surfaces are in a format this app version can't read (schemaVersion ${String(parsed.schemaVersion)}).`);
    }
    return {
        schemaVersion: SURFACES_SCHEMA_VERSION,
        surfaces: parsed.surfaces,
        groups: Array.isArray(parsed.groups) ? parsed.groups : [],
        ...washFlags(parsed.wash === true, parsed.washOnly === true),
        ...(typeof parsed.approvedReason === 'string' && parsed.approvedReason && { approvedReason: parsed.approvedReason }),
        ...((0, exports.isHoursById)(parsed.approvedAssessed) && { approvedAssessed: parsed.approvedAssessed }),
    };
}
/** Hours by surface id: an object of finite, non-negative numbers. */
const isHoursById = (value) => typeof value === 'object' && value !== null && !Array.isArray(value) && Object.values(value).every((h) => typeof h === 'number' && Number.isFinite(h) && h >= 0);
exports.isHoursById = isHoursById;
/** Fix the PM's hours for Assessed, or clear them (null) when the REVIEW chooses Measured or Other. */
function setApprovedAssessed(doc, hours) {
    const { approvedAssessed: _old, ...rest } = doc;
    return hours ? { ...rest, approvedAssessed: hours } : rest;
}
/** Set or clear the reason for the area's Other hours. */
function setApprovedReason(doc, reason) {
    const { approvedReason: _old, ...rest } = doc;
    const text = reason.trim();
    return text ? { ...rest, approvedReason: text } : rest;
}
/** The flags as stored: only what is on, and Wash only always with Wash. */
function washFlags(wash, washOnly) {
    return { ...((wash || washOnly) && { wash: true }), ...(washOnly && { washOnly: true }) };
}
/** Without the wash flags, so they can be laid on again. */
function withoutWash(doc) {
    const { wash: _wash, washOnly: _only, ...rest } = doc;
    return rest;
}
/** Tick or untick Wash; off clears Wash only too. */
const setWash = (doc, on) => ({ ...withoutWash(doc), ...washFlags(on, on && doc.washOnly === true) });
exports.setWash = setWash;
/** Turn Wash only on (which ticks Wash) or off (Wash stays ticked). */
const setWashOnly = (doc, on) => ({ ...withoutWash(doc), ...washFlags(on || doc.wash === true, on) });
exports.setWashOnly = setWashOnly;
const serialiseSurfaces = (doc) => JSON.stringify({ ...doc, schemaVersion: SURFACES_SCHEMA_VERSION });
exports.serialiseSurfaces = serialiseSurfaces;
const newLine = (from) => ({ id: crypto.randomUUID(), b: from?.b ?? null, qty: 1 });
function addSurface(doc, type, groupId) {
    const surface = {
        id: crypto.randomUUID(),
        typeId: type.id,
        typeName: type.name,
        method: type.method ?? '',
        unit: type.unit ?? '',
        ...(groupId && { groupId }),
        lines: [newLine()],
    };
    return { ...doc, surfaces: [...doc.surfaces, surface] };
}
const removeSurface = (doc, surfaceId) => ({
    ...doc,
    surfaces: doc.surfaces.filter((s) => s.id !== surfaceId),
});
exports.removeSurface = removeSurface;
function mapSurface(doc, surfaceId, change) {
    return { ...doc, surfaces: doc.surfaces.map((s) => (s.id === surfaceId ? change(s) : s)) };
}
/** Lay `change` over an object; a key set to `undefined` is removed (put back to the default). */
function withChanges(base, change) {
    const next = { ...base, ...change };
    for (const key of Object.keys(change)) {
        if (change[key] === undefined)
            delete next[key];
    }
    return next;
}
/** Set or clear overrides; `undefined` puts a setting back to the type's default. */
const updateSettings = (doc, surfaceId, settings) => mapSurface(doc, surfaceId, (s) => withChanges(s, settings));
exports.updateSettings = updateSettings;
/** Rename a surface for this job; blank (or the type's own name) goes back to the type's name. */
function renameSurface(doc, surfaceId, name) {
    const trimmed = name.trim();
    return mapSurface(doc, surfaceId, (s) => withChanges(s, { name: trimmed && trimmed !== s.typeName ? trimmed : undefined }));
}
/** A custom line's unit (#417, units.ts). A new unit puts girth back to the type's default: a width in metres isn't an item's m². */
const setUnit = (doc, surfaceId, unit) => mapSurface(doc, surfaceId, (s) => (s.unit === unit ? s : withChanges(s, { unit, girth: undefined })));
exports.setUnit = setUnit;
/** Flag the surface's paint as non-standard (with a note), or `undefined` to clear the flag. */
const setPaintFlag = (doc, surfaceId, flag) => mapSurface(doc, surfaceId, (s) => withChanges(s, { paintFlag: flag && (flag.note?.trim() ? { note: flag.note.trim() } : {}) }));
exports.setPaintFlag = setPaintFlag;
/** The PM's assessed hours and note for a surface; `undefined` clears one. */
const updateAssessment = (doc, surfaceId, change) => mapSurface(doc, surfaceId, (s) => withChanges(s, change));
exports.updateAssessment = updateAssessment;
/** A new line copies the previous one's second dimension (a run of walls only needs lengths). */
const addLine = (doc, surfaceId) => mapSurface(doc, surfaceId, (s) => ({ ...s, lines: [...s.lines, newLine(s.lines[s.lines.length - 1])] }));
exports.addLine = addLine;
const updateLine = (doc, surfaceId, lineId, change) => mapSurface(doc, surfaceId, (s) => ({ ...s, lines: s.lines.map((l) => (l.id === lineId ? { ...l, ...change } : l)) }));
exports.updateLine = updateLine;
const removeLine = (doc, surfaceId, lineId) => mapSurface(doc, surfaceId, (s) => ({ ...s, lines: s.lines.filter((l) => l.id !== lineId) }));
exports.removeLine = removeLine;
// ---------- repeat groups ----------
function addGroup(doc, name) {
    return { ...doc, groups: [...doc.groups, { id: crypto.randomUUID(), name, count: 1 }] };
}
const updateGroup = (doc, groupId, change) => ({
    ...doc,
    groups: doc.groups.map((g) => (g.id === groupId ? { ...g, ...change } : g)),
});
exports.updateGroup = updateGroup;
/** Removing a group removes its surfaces too — they were measured as part of it. */
const removeGroup = (doc, groupId) => ({
    ...doc,
    groups: doc.groups.filter((g) => g.id !== groupId),
    surfaces: doc.surfaces.filter((s) => s.groupId !== groupId),
});
exports.removeGroup = removeGroup;
/** A surface with new ids for it and its lines, in `groupId` (or none); the PM's assessment is not copied. */
function copySurface(surface, groupId) {
    const { assessedHours: _hours, assessNote: _note, groupId: _group, ...rest } = surface;
    return {
        ...rest,
        id: crypto.randomUUID(),
        ...(groupId && { groupId }),
        lines: surface.lines.map((l) => ({ ...l, id: crypto.randomUUID() })),
    };
}
/** "Balcony (copy)", count 1, with copies of the group's surfaces — for a second balcony type. */
function duplicateGroup(doc, groupId) {
    const source = doc.groups.find((g) => g.id === groupId);
    if (!source)
        return doc;
    const copy = { id: crypto.randomUUID(), name: `${source.name} (copy)`, count: 1 };
    const surfaces = doc.surfaces.filter((s) => s.groupId === groupId).map((s) => copySurface(s, copy.id));
    return { ...doc, groups: [...doc.groups, copy], surfaces: [...doc.surfaces, ...surfaces] };
}
/** The whole document with new ids throughout, for a duplicated area. Assessed hours and notes are not copied; the wash flags are. */
function copySurfaces(doc) {
    const groupIds = new Map(doc.groups.map((g) => [g.id, crypto.randomUUID()]));
    return {
        schemaVersion: SURFACES_SCHEMA_VERSION,
        ...washFlags(doc.wash === true, doc.washOnly === true),
        groups: doc.groups.map((g) => ({ ...g, id: groupIds.get(g.id) })),
        surfaces: doc.surfaces.map((s) => copySurface(s, s.groupId ? groupIds.get(s.groupId) : undefined)),
    };
}
/** How many times a surface counts: its group's count, else 1. A missing group counts once. */
function surfaceCount(doc, surface) {
    if (!surface.groupId)
        return 1;
    const count = doc.groups.find((g) => g.id === surface.groupId)?.count;
    return typeof count === 'number' && count > 0 ? count : 1;
}
